import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {UntypedFormBuilder} from '@angular/forms';
import {SearchService} from '../../../../services/search/search.service';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, pairwise, startWith} from 'rxjs/operators';
import {SearchWidgetV3StateService} from '../../services/search-widget-v3-state.service';
import {SearchWidgetV3ManagerService} from '../../services/search-widget-v3-manager.service';
import {SearchWidgetLabelsEnum} from '../../../search-widget-v2/enums/search-widget-labels.enum';

@Component({
  selector: 'app-search-widget-v3-places-form',
  templateUrl: './search-widget-v3-places-form.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV3PlacesFormComponent implements OnInit, OnDestroy, AfterViewInit {
  public shouldGetFocus = input<boolean>();

  fieldFocused = output<boolean>();
  searchByLocationTrigger = output<boolean>();
  updateSuggestions = output<string>();

  public isTermPlacesNotEmpty = signal<boolean>(false);
  @ViewChild('placesInput') protected placesInput;
  protected placeForm;
  private subscriptions: Subscription[] = [];
  private searchService = inject(SearchService);
  public isLocationLoading = this.searchService.isLocationLoading;
  private widgetState = inject(SearchWidgetV3StateService);
  public placesFieldValue = this.widgetState.placesFieldValue;

  private searchWidgetManagerService = inject(SearchWidgetV3ManagerService);
  private formBuilder = inject(UntypedFormBuilder);

  constructor() {
    this.buildPlaceForm();

    effect(
      () => {
        if (this.placesFieldValue()) {
          this.updatePlaceFieldValues();
        }
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit(): void {
    this.setListeners();
    this.updatePlaceFieldValues();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public ngAfterViewInit() {
    if (this.shouldGetFocus()) {
      this.setFieldFocus();
    }
  }

  protected onPlaceFocus($event) {
    this.searchWidgetManagerService.loadPlaces();
    this.fieldFocused.emit(true);
    $event.stopPropagation();
  }

  protected onPlaceClear(): void {
    this.placeForm.reset({
      term: '',
    });
  }

  protected onPlaceEnterHit(): void {
    if (this.placeForm.controls['term'].value !== '') {
      this.searchWidgetManagerService.onPlaceFieldEnterHit(this.placeForm.controls['term'].value);
      this.placeForm.nativeElement.blur();
    }
  }

  private setFieldFocus(): void {
    this.placesInput.nativeElement.focus();
  }

  private setListeners() {
    this.subscriptions.push(
      this.placeForm.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged(), startWith(''), pairwise())
        .subscribe(([previous, current]) => {
          const previousTerm = previous.term;
          const currentTerm = current.term;

          if (previousTerm !== currentTerm && currentTerm !== SearchWidgetLabelsEnum.NARA_MIG) {
            this.widgetState.updateNewPlacesFieldValue(currentTerm);
          }
        }),
    );
  }

  private buildPlaceForm() {
    this.placeForm = this.formBuilder.group(
      {
        term: [''],
      },
      {emitEvent: false},
    );
  }

  private updatePlaceFieldValues(): void {
    if (this.placeForm.controls['term'].value !== this.placesFieldValue()) {
      this.placeForm.setValue(
        {
          term: this.placesFieldValue() || '',
        },
        {emitEvent: false},
      );
    }
    this.isTermPlacesNotEmpty.set(this.placeForm.controls['term'].value !== '');
  }
}
